import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';

import { CONFIG } from '../../config';
import {
  DEAL_ANNOUNCEMENT_DECK_TYPE,
  OTHER_DECK_TYPE,
  SlideDeckLinkResource,
  SlideDeckResource
} from './slide-deck-comparison.model';

/**
 * The subset of a comparison resource {@link resolveManualLabel} needs. Declared structurally rather than as a
 * union so the one helper serves `SlideDeckComparisonResource` (the report page) and `SlideDeckComparisonMeta`
 * (both list surfaces) without either importing the other's shape.
 */
export interface ManuallyGeneratedFields {
  manuallyGenerated: boolean | null;
  requestedByFirstName: string | null;
  requestedByLastName: string | null;
}

/**
 * Full-label translation keys for the known deck-type taxonomy (DECK-TYPE-SERIES-SPEC), keyed by the raw
 * backend `deck_type` value. A value outside this map still renders via {@link humanizeDeckType}. Shared here
 * rather than per-component so the Decks modal and the self-service request modal cannot drift.
 */
export const DECK_TYPE_LABEL_KEYS: Record<string, string> = {
  corporate_presentation: 'SLIDE_DECK_COMPARISON.DECKS_MODAL.TYPE_CORPORATE_PRESENTATION',
  earnings: 'SLIDE_DECK_COMPARISON.DECKS_MODAL.TYPE_EARNINGS',
  conference: 'SLIDE_DECK_COMPARISON.DECKS_MODAL.TYPE_CONFERENCE',
  rd_day: 'SLIDE_DECK_COMPARISON.DECKS_MODAL.TYPE_RD_DAY',
  clinical_data: 'SLIDE_DECK_COMPARISON.DECKS_MODAL.TYPE_CLINICAL_DATA',
  scientific: 'SLIDE_DECK_COMPARISON.DECKS_MODAL.TYPE_SCIENTIFIC',
  [DEAL_ANNOUNCEMENT_DECK_TYPE]: 'SLIDE_DECK_COMPARISON.DECKS_MODAL.TYPE_DEAL_ANNOUNCEMENT',
  [OTHER_DECK_TYPE]: 'SLIDE_DECK_COMPARISON.DECKS_MODAL.TYPE_OTHER'
};

/** Short-label keys; only entries whose short form differs from the full label need one (see {@link DECK_TYPE_LABEL_KEYS}). */
export const DECK_TYPE_SHORT_LABEL_KEYS: Record<string, string> = {
  corporate_presentation: 'SLIDE_DECK_COMPARISON.DECKS_MODAL.TYPE_CORPORATE_PRESENTATION_SHORT',
  clinical_data: 'SLIDE_DECK_COMPARISON.DECKS_MODAL.TYPE_CLINICAL_DATA_SHORT',
  [DEAL_ANNOUNCEMENT_DECK_TYPE]: 'SLIDE_DECK_COMPARISON.DECKS_MODAL.TYPE_DEAL_ANNOUNCEMENT_SHORT'
};

/**
 * Resolves a deck type to its display label: a known taxonomy value uses its translated label, an
 * off-taxonomy value is humanized, and an absent one becomes the "miscellaneous" label. `short` selects the
 * compact form used in inline row badges and tab labels (e.g. "Clinical" for `clinical_data`), falling back to
 * the full label for a type with no distinct short form.
 * @param {string | null} deckType the raw backend deck-type value
 * @param {TranslateService} translate the translate service used to resolve the label key
 * @param {boolean} [short] whether to prefer the short label form
 * @returns {string} the resolved display label
 */
export function resolveDeckTypeLabel(deckType: string | null, translate: TranslateService, short = false): string {
  if (!deckType) {
    return translate.instant('SLIDE_DECK_COMPARISON.DECKS_MODAL.TYPE_MISC');
  }
  const key = short
    ? (DECK_TYPE_SHORT_LABEL_KEYS[deckType] ?? DECK_TYPE_LABEL_KEYS[deckType])
    : DECK_TYPE_LABEL_KEYS[deckType];
  // `deckType` is non-null by the guard above, so humanizing it always yields a label for an off-taxonomy value.
  return key ? translate.instant(key) : humanizeDeckType(deckType) as string;
}

/**
 * Builds the "Manual · <name>" provenance indicator for a comparison an analyst requested through self service
 * (RA-2251 Req8), or null for one produced by the automated pipeline. The requester's name is omitted when the
 * backend has none to give — the comparison is still flagged manual in that case — and their email is
 * deliberately never exposed. Shared by the report byline and both list surfaces so the three cannot drift.
 * @param {ManuallyGeneratedFields | null} record the comparison resource or metadata record
 * @param {TranslateService} translate the translate service used to resolve the indicator copy
 * @returns {string | null} the indicator text, or null for an automated comparison
 */
export function resolveManualLabel(record: ManuallyGeneratedFields | null, translate: TranslateService): string | null {
  if (!record?.manuallyGenerated) {
    return null;
  }
  const indicator = translate.instant('SLIDE_DECK_COMPARISON.SELF_SERVICE.MANUAL_INDICATOR');
  const requester = joinPersonName(record.requestedByFirstName, record.requestedByLastName);
  return requester ? `${indicator} · ${requester}` : indicator;
}

/**
 * Joins a person's first and last name into a display name, skipping either part the backend has no value for
 * and returning null when it has neither. Shared by {@link resolveManualLabel} and the Decks modal's
 * `Uploaded By` column, so a requester and an uploader are never formatted two different ways.
 * @param {string | null} firstName the person's first name
 * @param {string | null} lastName the person's last name
 * @returns {string | null} the joined name, or null when there is nothing to show
 */
export function joinPersonName(firstName: string | null, lastName: string | null): string | null {
  return [firstName, lastName].filter(part => !!part).join(' ') || null;
}

/**
 * Returns the OLD or NEW deck from a comparison's normalized `slideDecks` links (RA-2079), or null
 * when that side is absent. Shared by {@link deckLabelByStatus} (display label) and callers that also
 * need the deck's sortable `date` (e.g. lineage-span computation, which sorts by `date` but displays
 * `dateLabel` — the two are not the same string; see DECK-TYPE-SERIES-SPEC).
 * @param {Array<SlideDeckLinkResource> | null} decks the comparison's slide-deck links
 * @param {'OLD' | 'NEW'} status which side to find
 * @returns {SlideDeckResource | null} the matching deck, or null
 */
export function findDeckByStatus(decks: Array<SlideDeckLinkResource> | null, status: 'OLD' | 'NEW'): SlideDeckResource | null {
  return decks?.find(link => link.deckStatus === status)?.slideDeck ?? null;
}

/**
 * Returns the display name of the OLD or NEW deck from a comparison's normalized `slideDecks` links
 * (RA-2079) — TARA's verbatim `displayName`, else the legacy `dateLabel` + `subject` composition
 * (see {@link resolveDeckDisplayName}, RA-2734) — or null when that side is absent. Used by the Company
 * Decks modal's Comparisons table and the company-page expansion panel to render the "old -> new"
 * lineage now that the flat `oldDeckLabel` / `newDeckLabel` columns are gone.
 * @param {Array<SlideDeckLinkResource> | null} decks the comparison's slide-deck links
 * @param {'OLD' | 'NEW'} status which side's label to read
 * @returns {string | null} the matching deck's display name, or null
 */
export function deckLabelByStatus(decks: Array<SlideDeckLinkResource> | null, status: 'OLD' | 'NEW'): string | null {
  return resolveDeckDisplayName(findDeckByStatus(decks, status));
}

/**
 * A deck's on-screen name: TARA's finished `displayName` when it has one, else the legacy
 * `dateLabel` + `subject` composition (RA-2734).
 *
 * 🔴 `displayName` is rendered **verbatim** — it already carries TARA's per-company-unique " (n)"
 * suffix, so it must not be parsed, split or recomposed. The fallback exists because the field is
 * nullable for two real reasons: every deck registered before RA-2734 has no value for it (there is
 * deliberately no backfill), and TARA's naming is best-effort and omits the field when it fails.
 * A blank or whitespace-only value is treated exactly as an absent one, for the reason
 * {@link combineDeckLabel} records: these are free-text fields the backend does not normalise, and a
 * truthy '   ' slips through every downstream `??`/`||` fallback and reaches the DOM as an
 * `aria-label="   "`.
 *
 * 🔴 Not to be confused with `DeckListStore.deckDisplayName`, a private row-to-dialog-name chain that
 * falls all the way through to `filename`, nor with the `DeckRow.displayName` field this feeds.
 * @param {SlideDeckResource | null} deck the deck to name
 * @returns {string | null} the deck's display name, or null when there is nothing to show
 */
export function resolveDeckDisplayName(deck: SlideDeckResource | null): string | null {
  return deck?.displayName?.trim() || combineDeckLabel(deck?.dateLabel ?? null, deck?.subject ?? null);
}

/**
 * Returns the upstream source filename of the OLD or NEW deck from a comparison's normalized
 * `slideDecks` links (RA-2079), or null when that side is absent. Used as the tooltip content for
 * {@link deckLabelByStatus}'s display name, since the name alone does not always uniquely identify
 * the underlying file.
 * @param {Array<SlideDeckLinkResource> | null} decks the comparison's slide-deck links
 * @param {'OLD' | 'NEW'} status which side's filename to read
 * @returns {string | null} the matching deck's filename, or null
 */
export function deckFilenameByStatus(decks: Array<SlideDeckLinkResource> | null, status: 'OLD' | 'NEW'): string | null {
  return findDeckByStatus(decks, status)?.filename ?? null;
}

/**
 * Returns when the OLD or NEW deck of a comparison became available in RMS (its `uploaded` timestamp), or
 * null when that side is absent. Rendered as the second line of {@link deckFilenameByStatus}'s tooltip, so a
 * reader can tell two same-labelled decks apart by when each was uploaded.
 *
 * 🔴 `uploaded`, not the audit `created`, since RA-2731: a deck that was deleted and restored genuinely
 * became available again, and every surface labelled "Uploaded" has to agree on which clock it means.
 * @param {Array<SlideDeckLinkResource> | null} decks the comparison's slide-deck links
 * @param {'OLD' | 'NEW'} status which side's upload timestamp to read
 * @returns {string | null} the matching deck's `uploaded` timestamp, or null
 */
export function deckUploadedByStatus(decks: Array<SlideDeckLinkResource> | null, status: 'OLD' | 'NEW'): string | null {
  return findDeckByStatus(decks, status)?.uploaded ?? null;
}

/**
 * The suffix appended to the NEW side of a comparison title whose two deck labels are otherwise identical;
 * see {@link disambiguateNewDeckLabel}.
 */
export const DUPLICATE_DECK_LABEL_SUFFIX = ' (1)';

/**
 * Disambiguates the NEW side of an "old -> new" comparison title: when both sides render the *same* string,
 * the NEW label comes back with {@link DUPLICATE_DECK_LABEL_SUFFIX} appended ("2025-06 -> 2025-06 (1)"), so a
 * title that would otherwise read as one label repeated tells the reader which side is which. Any other case —
 * either side absent, or two labels that already differ — returns `newLabel` untouched.
 *
 * 🔴 The comparison is between the **rendered** labels, whatever the calling surface renders: the combined
 * `dateLabel + subject` form on the report header and both list surfaces, the bare `dateLabel` on the tight
 * Side-by-Side rail headers. Keying off the raw `dateLabel` everywhere instead would suffix
 * "2025-06 Investor Day -> 2025-06 Analyst Day", a title that is already unambiguous on screen. Both sides are
 * trimmed before comparing because the underlying fields are free text from analyst uploads and the TARA
 * harvest (see {@link combineDeckLabel}).
 * @param {string | null} oldLabel the OLD side's rendered label
 * @param {string | null} newLabel the NEW side's rendered label
 * @returns {string | null} the NEW label, suffixed when it is indistinguishable from the OLD one
 */
export function disambiguateNewDeckLabel(oldLabel: string | null, newLabel: string | null): string | null {
  if (!oldLabel || !newLabel || oldLabel.trim() !== newLabel.trim()) {
    return newLabel;
  }
  return `${newLabel}${DUPLICATE_DECK_LABEL_SUFFIX}`;
}

/**
 * Combines a deck's date label with its subject for display: `subject` appended after `dateLabel`
 * with a space (e.g. "2025-06 Investor Day"). Falls back to whichever of the two is present alone, and
 * to null when both are absent.
 *
 * 🔴 **Both** sides are trimmed, and a blank/whitespace-only value is treated exactly as an absent one.
 * The asymmetry this replaced — trimming `subject` but returning `dateLabel` verbatim — meant a
 * whitespace-only `dateLabel` came back as a truthy `'   '` that every downstream `??`/`||` fallback then
 * skipped. That is how a whitespace-only label reached an `aria-label` and produced a checkbox with no
 * accessible name. Both fields are free text from analyst uploads and the TARA harvest, so neither can be
 * assumed clean.
 * @param {string | null} dateLabel the deck's date label
 * @param {string | null} subject the deck's free-text subject/series tag
 * @returns {string | null} the combined display label, or null when there is nothing to show
 */
export function combineDeckLabel(dateLabel: string | null, subject: string | null): string | null {
  const trimmedDate = dateLabel?.trim() || null;
  const trimmedSubject = subject?.trim() || null;
  if (!trimmedDate) {
    return trimmedSubject;
  }
  return trimmedSubject ? `${trimmedDate} ${trimmedSubject}` : trimmedDate;
}

/**
 * Humanizes a free-form, AI-classified deck-type/category value into a Title Case label
 * (e.g. `CORPORATE_DECK` -> `Corporate Deck`), or null when the type is absent. The caller decides what
 * to render for the null case (the modal/panel substitute a "miscellaneous" label). Splitting on
 * underscores, hyphens and whitespace keeps it stable across the backend's various separators.
 * @param {string | null} deckType the raw deck-type value
 * @returns {string | null} the humanized label, or null when there is no type
 */
export function humanizeDeckType(deckType: string | null): string | null {
  if (!deckType) {
    return null;
  }
  return deckType
    .toLowerCase()
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Formats a comparison timestamp as a short ET date (`MM/dd/yyyy`), or '' when absent/invalid. The
 * list endpoint emits scalar columns (`generatedAt`, `created`) in the server datetime format
 * (space-separated, no zone), so they are parsed as UTC in that format first, with an ISO-8601
 * fallback; either way the instant is converted to ET to match the app-wide convention.
 * @param {string | null} raw the timestamp string
 * @returns {string} the formatted MM/dd/yyyy date, or '' when there is nothing valid to show
 */
export function formatComparisonDate(raw: string | null): string {
  const dt = parseComparisonDate(raw);
  return dt && dt.isValid ? dt.setZone(CONFIG.formatEST).toFormat(CONFIG.shortDateFormat) : '';
}

/**
 * Formats a deck's own extracted `date` — a date-only `LocalDate` (`yyyy-MM-dd`), not an instant — as
 * `MM/dd/yyyy`, with **no timezone conversion**. Running a date-only value through the app's UTC→ET
 * convention shifts it a day backwards (`2025-06-01` renders as 05/31/2025), which is wrong for a value that
 * never had a time in the first place. Falls back to {@link formatComparisonDate} for anything that is not a
 * bare date, and to '' when there is nothing valid to show.
 * @param {string | null} raw the deck's `date`
 * @returns {string} the formatted MM/dd/yyyy date, or ''
 */
export function formatDeckDate(raw: string | null): string {
  if (!raw) {
    return '';
  }
  const dateOnly = DateTime.fromFormat(raw, 'yyyy-MM-dd');
  return dateOnly.isValid ? dateOnly.toFormat(CONFIG.shortDateFormat) : formatComparisonDate(raw);
}

/**
 * Formats a comparison timestamp as a written-out ET date (`LLL d, yyyy`, e.g. "Jan 13, 2026"), or ''
 * when absent/invalid. Used by the mobile Company Decks modal rows; the desktop table keeps the short
 * {@link formatComparisonDate} (MM/dd/yyyy) form. Parsing/zone handling matches {@link formatComparisonDate}.
 * @param {string | null} raw the timestamp string
 * @returns {string} the formatted "LLL d, yyyy" date, or '' when there is nothing valid to show
 */
export function formatComparisonDateLong(raw: string | null): string {
  const dt = parseComparisonDate(raw);
  return dt && dt.isValid ? dt.setZone(CONFIG.formatEST).toFormat('LLL d, yyyy') : '';
}

/**
 * Formats a timestamp as a full, human-legible ET instant — `LLL d, yyyy, h:mm a` plus an explicit ` ET` zone
 * label, e.g. "Aug 25, 2026, 3:42 PM ET".
 *
 * This is the hover form of the date columns, whose cells only ever show the day; the time of day is the part
 * a day-only cell cannot tell you. The zone label is not decoration: the value is stored UTC and converted
 * here, so without it the reader has no way to know which clock the time is on (see the feature guide's dates
 * convention). Shared with the report byline, which renders the same instant in the same words — they were two
 * copies of this format before.
 *
 * Returns '' when there is nothing valid to show, which callers use as the signal to render **no** tooltip
 * rather than an empty one.
 * @param {string | null} raw the timestamp string
 * @returns {string} e.g. "Aug 25, 2026, 3:42 PM ET", or '' when absent/invalid
 */
export function formatComparisonDateTimeLong(raw: string | null): string {
  const dt = parseComparisonDate(raw);
  return dt && dt.isValid ? `${dt.setZone(CONFIG.formatEST).toFormat('LLL d, yyyy, h:mm a')} ET` : '';
}

/**
 * The epoch milliseconds for a comparison/deck timestamp, or 0 when absent or unparseable — so a value with no
 * usable date sorts last under a descending sort rather than ahead of everything. Shared by the Decks modal and
 * the comparison grouping service, which must agree on the ordering key or a group can sort ahead of another
 * while its own newest row sorts behind.
 * @param {string | null} raw the timestamp string
 * @returns {number} epoch millis, or 0
 */
export function toComparisonMillis(raw: string | null): number {
  const dt = parseComparisonDate(raw);
  return dt && dt.isValid ? dt.toMillis() : 0;
}

/**
 * The subset of a comparison record {@link resolveComparisonSortMillis} needs. Declared structurally, like
 * {@link ManuallyGeneratedFields}, so the one helper serves `SlideDeckComparisonMeta` (both list surfaces)
 * without the util importing either caller's row shape.
 */
export interface ComparisonSortFields {
  slideDecks: Array<SlideDeckLinkResource> | null;
  generatedAt: string | null;
}

/**
 * Req11's ordering key for a comparison: the newer of its two compared decks' own `date`s, so a comparison
 * list reads chronologically by what the decks actually are rather than by when the AI happened to generate
 * the report. Falls back to `generatedAt` when neither side has a parseable date.
 *
 * 🔴 Shared rather than per-surface, and deliberately so. Every surface that presents "the newest
 * comparison" has to agree on what newest means: the Decks modal's Comparisons list, its `isLatest` marker
 * (via {@link ComparisonGroupingService}) and the company panel's 3-row preview all sort on this one key.
 * When the panel sorted independently — by arrival order, `id DESC` — its top row and the modal's
 * `isLatest` row named different comparisons for the same company.
 *
 * ⚠️ Not the same question as the "New" badge, which is about ARRIVAL and correctly reads the
 * highest `id` plus `generatedAt` instead; see `DeckComparisonNewBadgeService`.
 * @param {ComparisonSortFields} record the comparison metadata record
 * @returns {number} the sort key in epoch millis, or 0 when nothing is parseable
 */
export function resolveComparisonSortMillis(record: ComparisonSortFields): number {
  const oldDate = findDeckByStatus(record.slideDecks, 'OLD')?.date ?? null;
  const newDate = findDeckByStatus(record.slideDecks, 'NEW')?.date ?? null;
  const newest = Math.max(toComparisonMillis(oldDate), toComparisonMillis(newDate));
  return newest > 0 ? newest : toComparisonMillis(record.generatedAt);
}

/**
 * Parses a server-datetime / ISO-8601 comparison timestamp to a luxon DateTime in UTC, or null when
 * absent/invalid. Tries the server format (`yyyy-MM-dd HH:mm:ss.S`) first, then ISO-8601.
 * @param {string | null} raw the timestamp string
 * @returns {DateTime | null} the parsed UTC DateTime, or null
 */
export function parseComparisonDate(raw: string | null): DateTime | null {
  if (!raw) {
    return null;
  }
  const dt = DateTime.fromFormat(raw, CONFIG.dateTimeServerFormat, { zone: CONFIG.timezoneUTC });
  if (dt.isValid) {
    return dt;
  }
  const iso = DateTime.fromISO(raw, { zone: CONFIG.timezoneUTC });
  return iso.isValid ? iso : null;
}
